import React from "react";
import { Img, Loop, OffthreadVideo, Sequence, useCurrentFrame } from "remotion";
import { mediaSrc } from "./assets";
import { GLYPHS as MARKER_GLYPHS } from "./markers.generated";
import { AD_GLYPHS } from "./adGlyphs.generated";
import { C } from "./brand";
import { BookedPill, ramp, eio, mix } from "./kit";

// Map marker vocabulary from stippl-knowledge design/map-markers (component-values.md, states.md,
// prototype.html) and markerVisualTokens.ts: fill by type, glyph ink #14202a, white ring,
// "photo on stop" card of 3.9 R with a 3 px white edge and radius 16, caption as a white pill.

export const INK = "#14202a";
export const FILL = {
	city: "#d6f0e2",
	town: "#fbe7bf",
	hotels: "#d6f0e2",
	food: "#e5daf8",
	todo: "#fbe7bf",
	outdoors: "#cfe6f8",
};

const GLYPHS = { ...MARKER_GLYPHS, ...AD_GLYPHS };

export const Glyph: React.FC<{ name: string; size: number; color?: string }> = ({ name, size, color = INK }) => {
	const g = GLYPHS[name];
	return (
		<svg width={size} height={size} viewBox={g.vb} style={{ color, display: "block" }} fill="currentColor" dangerouslySetInnerHTML={{ __html: g.inner }} />
	);
};

// src: an https URL or a path in the public dir (see mediaSrc)
export type MediaSrc = { src: string; video?: boolean; clipFrames?: number; pos?: string };

// a picture or a looping clip that starts playing when it appears
export const Media: React.FC<{ m: MediaSrc; from: number; zoom?: number }> = ({ m, from, zoom = 1 }) => {
	const st: React.CSSProperties = {
		width: "100%",
		height: "100%",
		objectFit: "cover",
		objectPosition: m.pos ?? "50% 50%",
		display: "block",
		transform: `scale(${zoom})`,
	};
	if (!m.video) return <Img src={mediaSrc(m.src)} style={st} />;
	return (
		<Sequence from={from} layout="none">
			<Loop durationInFrames={m.clipFrames ?? 150} layout="none">
				<OffthreadVideo src={mediaSrc(m.src)} muted style={st} />
			</Loop>
		</Sequence>
	);
};

// A stop or place marker, optionally with its photo card above it. (x, y) is the marker centre.
// t = 0..1 appearance (marker pops, then the card rises out of it), out = 0..1 leaving.
export const PhotoMarker: React.FC<{
	x: number;
	y: number;
	R: number;
	fill: string;
	glyph?: string;
	num?: number;
	media?: MediaSrc;
	mediaFrom: number;
	rot?: number;
	markerT: number;
	cardT: number;
	out?: number;
	caption?: string;
	captionT?: number;
	booked?: number;
	z?: number;
	bob?: number;
	cardDx?: number; // shift the photo card sideways when two stops sit close
	captionSide?: -1 | 0 | 1; // hang the caption left or right of the marker when two stops sit close
}> = ({
	x,
	y,
	R,
	fill,
	glyph,
	num,
	media,
	mediaFrom,
	rot = -3,
	markerT,
	cardT,
	out = 0,
	caption,
	captionT = 0,
	booked = 0,
	z = 6,
	bob = 0,
	cardDx = 0,
	captionSide = 0,
}) => {
	const f = useCurrentFrame();
	if (markerT <= 0 || out >= 1) return null;
	const o = 1 - out;
	const cs = Math.round(R * 3.9);
	const float = Math.sin((f + bob * 17) / 22) * R * 0.08;
	return (
		<div style={{ position: "absolute", left: x, top: y, width: 0, height: 0, zIndex: z, opacity: o }}>
			{media && cardT > 0 && (
				<div
					style={{
						position: "absolute",
						left: -cs / 2 + cardDx,
						top: -R * 0.62 - cs + float,
						width: cs,
						height: cs,
						borderRadius: 16 * (R / 23),
						border: `${3 * (R / 23)}px solid #fff`,
						boxShadow: `0 ${6 * (R / 23)}px ${15 * (R / 23)}px rgba(15,62,74,.35)`,
						overflow: "hidden",
						background: "#fff",
						transformOrigin: "50% 100%",
						transform: `translateY(${(1 - cardT) * R}px) scale(${0.3 + 0.7 * cardT}) rotate(${mix(cardT, rot * 4, rot)}deg)`,
						opacity: Math.min(1, cardT * 2.5),
					}}
				>
					<div style={{ width: "100%", height: "100%", borderRadius: 13 * (R / 23), overflow: "hidden" }}>
						<Media m={media} from={mediaFrom} />
					</div>
				</div>
			)}
			<div
				style={{
					position: "absolute",
					left: -R,
					top: -R,
					width: R * 2,
					height: R * 2,
					borderRadius: R,
					background: fill,
					border: `${2.5 * (R / 23)}px solid #fff`,
					boxShadow: `0 ${2 * (R / 23)}px ${7 * (R / 23)}px rgba(15,62,74,.45)`,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					boxSizing: "border-box",
					transform: `scale(${markerT})`,
				}}
			>
				{num !== undefined ? (
					<b style={{ font: `700 ${Math.round(R * 1.02)}px/1 Rubik`, color: INK, letterSpacing: "-.01em" }}>{num}</b>
				) : glyph ? (
					<Glyph name={glyph} size={R * 1.05} />
				) : null}
			</div>
			{caption && captionT > 0 && (
				<div
					style={{
						position: "absolute",
						left: 0,
						top: R + 10 * (R / 23),
						transform: `translateX(${captionSide === 0 ? "-50%" : captionSide < 0 ? `calc(-100% - ${R * 0.15}px)` : `${R * 0.15}px`}) translateY(${(1 - captionT) * 10}px)`,
						opacity: captionT,
						whiteSpace: "nowrap",
						font: `600 ${12.5 * (R / 23)}px Rubik`,
						color: C.ink,
						background: "#fff",
						borderRadius: 999,
						padding: `${3 * (R / 23)}px ${10 * (R / 23)}px`,
						border: `${2 * (R / 23)}px solid #fff`,
						boxShadow: `0 ${2 * (R / 23)}px ${5 * (R / 23)}px rgba(15,62,74,.3)`,
					}}
				>
					{caption}
				</div>
			)}
			{booked > 0 && (
				<div
					style={{
						position: "absolute",
						left: -39,
						top: R + 10 * (R / 23) + 26 * (R / 23),
						width: 78,
						height: 22,
						transformOrigin: "50% 0%",
						transform: `scale(${mix(booked, 5, 1.35 * (R / 23))}) rotate(${mix(booked, -14, 0)}deg)`,
						opacity: Math.min(1, booked * 3),
					}}
				>
					<BookedPill />
				</div>
			)}
		</div>
	);
};

// a small transport disc on a route leg
export const LegIcon: React.FC<{ x: number; y: number; size: number; glyph: string; t: number; o?: number }> = ({ x, y, size, glyph, t, o = 1 }) =>
	t <= 0 ? null : (
		<div
			style={{
				position: "absolute",
				left: x - size / 2,
				top: y - size / 2,
				width: size,
				height: size,
				borderRadius: size,
				background: "#fff",
				boxShadow: "0 4px 12px rgba(15,62,74,.22)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				transform: `scale(${t})`,
				opacity: o,
				zIndex: 7,
			}}
		>
			<Glyph name={glyph} size={size * 0.58} />
		</div>
	);

export const appear = (f: number, at: number, dur = 14) => ramp(f, at, at + dur, eio);
